import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Inbox, FolderKanban, Users, Trash2, RefreshCcw, ArrowLeft } from "lucide-react";
import AvailabilityPanel from "@/pages/admin/AvailabilityPanel";
import { reportNonCritical } from "@/lib/analytics";

const STATUS_LEAD = ["new", "contacted", "qualified", "closed"];
const STATUS_PROJECT = ["new", "reviewing", "scoped", "closed"];

// Admin v2: session lives in an httpOnly cookie set by POST /api/admin/login.
// No tokens stored in browser storage. The shared `api` axios instance has
// `withCredentials: true` so the cookie rides along on every request.
async function adminLogin(token) {
  await api.post("/admin/login", { token });
}

async function adminLogout() {
  try {
    await api.post("/admin/logout", {});
  } catch (err) {
    reportNonCritical(err, "admin.logout");
  }
}

async function adminCheckSession() {
  try {
    await api.get("/admin/session");
    return true;
  } catch {
    return false;
  }
}

const STATUS_DOT_COLOR = {
  open: "bg-emerald-400",
  limited: "bg-gold",
  closed: "bg-rose-400",
};

function dotClassFor(status) {
  return STATUS_DOT_COLOR[status] || STATUS_DOT_COLOR.limited;
}

function pulseClassFor(status) {
  // Closed = no pulse; otherwise gold pulse for open/limited.
  if (status === "closed") return null;
  return status === "open" ? "bg-emerald-400" : "bg-gold";
}const STATUS_COLOR = {
  new: "text-gold border-gold/40",
  contacted: "text-blue-300 border-blue-300/40",
  reviewing: "text-blue-300 border-blue-300/40",
  qualified: "text-green-300 border-green-300/40",
  scoped: "text-green-300 border-green-300/40",
  closed: "text-neutral-400 border-neutral-300",
};

function LoginCard({ onLogin }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    try {
      await adminLogin(token.trim());
      toast.success("Welcome back to the Studio.");
      onLogin();
    } catch (_err) {
      toast.error("Invalid admin token.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login"
        className="w-full max-w-md border border-neutral-200 bg-[#FAFAFA] p-10"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-gold transition mb-8"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>
        <p className="overline">Admin · Restricted</p>
        <h1 className="font-serif text-4xl mt-4 leading-tight">
          Studio
          <br />
          <span className="italic text-aurora">control room.</span>
        </h1>
        <p className="text-neutral-500 mt-4 text-sm leading-relaxed">
          Enter the admin token to view incoming leads & project briefs.
        </p>
        <div className="mt-8">
          <label className="overline block mb-3" htmlFor="admin-token">
            Admin token
          </label>
          <input
            id="admin-token"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="editorial-input"
            placeholder="••••••••"
            data-testid="admin-token-input"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="btn-gold w-full justify-center mt-8 disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {busy ? "Verifying..." : "Enter studio"}
        </button>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-neutral-200 bg-[#FAFAFA] p-6">
      <div className="flex items-center justify-between mb-4">
        <Icon className="text-gold" size={18} strokeWidth={1.5} />
        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
          {sub}
        </span>
      </div>
      <div className="font-serif text-4xl text-neutral-900">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mt-2">
        {label}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminPage() {
  // null = checking cookie, false = signed out, true = signed in
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cookie probe on mount: if we already have a valid admin session, skip login.
  useEffect(() => {
    let cancelled = false;
    adminCheckSession().then((ok) => {
      if (!cancelled) setAuthed(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/leads"),
        api.get("/projects"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setProjects(p.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        setAuthed(false);
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const updateLeadStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
      load();
    } catch {
      toast.error("Failed to update lead.");
    }
  };

  const updateProjectStatus = async (id, status) => {
    try {
      await api.patch(`/projects/${id}`, { status });
      setProjects((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
      load();
    } catch {
      toast.error("Failed to update project.");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((arr) => arr.filter((x) => x.id !== id));
      load();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project brief permanently?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((arr) => arr.filter((x) => x.id !== id));
      load();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const logout = async () => {
    await adminLogout();
    // Best-effort cleanup of legacy storage from older builds.
    try {
      window.localStorage.removeItem("dbr_admin_token");
      window.sessionStorage.removeItem("gks_admin_token");
    } catch (err) {
      reportNonCritical(err, "admin.legacyCleanup");
    }
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div
        data-testid="admin-checking"
        className="min-h-screen flex items-center justify-center bg-white text-neutral-400 text-xs uppercase tracking-[0.2em]"
      >
        Checking session...
      </div>
    );
  }
  if (!authed) return <LoginCard onLogin={() => setAuthed(true)} />;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-white">
      {/* Top bar */}
      <header className="border-b border-neutral-200 bg-[#FAFAFA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <Link
              to="/"
              className="font-serif text-xl text-neutral-900 hover:text-gold transition"
            >
              GoldKey
            </Link>
            <span className="font-serif italic text-xl text-gold">
              Studio
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 ml-3">
              · Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              data-testid="admin-refresh"
              className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-gold inline-flex items-center gap-2 px-3 py-2 border border-neutral-200"
            >
              <RefreshCcw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-gold inline-flex items-center gap-2 px-3 py-2 border border-neutral-200"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Total leads"
            value={stats?.leads_total ?? "-"}
            sub="all-time"
          />
          <StatCard
            icon={Inbox}
            label="New leads"
            value={stats?.leads_new ?? "-"}
            sub="awaiting"
          />
          <StatCard
            icon={FolderKanban}
            label="Project briefs"
            value={stats?.projects_total ?? "-"}
            sub="all-time"
          />
          <StatCard
            icon={Inbox}
            label="New briefs"
            value={stats?.projects_new ?? "-"}
            sub="awaiting"
          />
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
        <div className="flex gap-1 border-b border-neutral-200 mb-6">
          <button
            onClick={() => setTab("leads")}
            data-testid="admin-tab-leads"
            className={`px-6 py-4 text-xs uppercase tracking-[0.2em] transition border-b-2 -mb-px ${
              tab === "leads"
                ? "border-gold text-gold"
                : "border-transparent text-neutral-500 hover:text-neutral-900"
            }`}
          >
            Leads ({leads.length})
          </button>
          <button
            onClick={() => setTab("projects")}
            data-testid="admin-tab-projects"
            className={`px-6 py-4 text-xs uppercase tracking-[0.2em] transition border-b-2 -mb-px ${
              tab === "projects"
                ? "border-gold text-gold"
                : "border-transparent text-neutral-500 hover:text-neutral-900"
            }`}
          >
            Project Briefs ({projects.length})
          </button>
          <button
            onClick={() => setTab("availability")}
            data-testid="admin-tab-availability"
            className={`px-6 py-4 text-xs uppercase tracking-[0.2em] transition border-b-2 -mb-px ${
              tab === "availability"
                ? "border-gold text-gold"
                : "border-transparent text-neutral-500 hover:text-neutral-900"
            }`}
          >
            Availability
          </button>
        </div>

        {loading && (
          <div className="text-neutral-500 text-sm">Loading...</div>
        )}

        {tab === "leads" && (
          <div className="border border-neutral-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 bg-[#FAFAFA]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Interest</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-t border-neutral-200/70 hover:bg-white"
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-4 text-neutral-500 whitespace-nowrap">
                      {fmtDate(l.created_at)}
                    </td>
                    <td className="px-4 py-4 text-neutral-900">{l.name}</td>
                    <td className="px-4 py-4">
                      <a
                        href={`mailto:${l.email}`}
                        className="text-gold hover:underline"
                      >
                        {l.email}
                      </a>
                      {l.message && (
                        <div className="text-neutral-500 text-xs mt-1 max-w-xs line-clamp-2">
                          {l.message}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-neutral-500">
                      {l.company || "-"}
                    </td>
                    <td className="px-4 py-4 text-neutral-500">
                      {l.interest || "-"}
                    </td>
                    <td className="px-4 py-4 text-neutral-500 text-xs">
                      {l.source}
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={l.status}
                        onChange={(e) => updateLeadStatus(l.id, e.target.value)}
                        className={`bg-transparent border px-2 py-1 text-xs uppercase tracking-wider ${STATUS_COLOR[l.status]}`}
                        data-testid={`lead-status-${l.id}`}
                      >
                        {STATUS_LEAD.map((s) => (
                          <option key={s} value={s} className="bg-white">
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => deleteLead(l.id)}
                        className="text-neutral-400 hover:text-red-400 transition"
                        aria-label="Delete"
                        data-testid={`lead-delete-${l.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && !loading && (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center text-neutral-400">
                      No leads yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "projects" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="projects-grid">
            {projects.map((p) => (
              <div
                key={p.id}
                className="border border-neutral-200 bg-[#FAFAFA] p-6"
                data-testid={`project-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-400">
                      {fmtDate(p.created_at)} · {p.project_type}
                    </p>
                    <h3 className="font-serif text-2xl mt-2 text-neutral-900">
                      {p.name}{" "}
                      {p.company && (
                        <span className="text-neutral-500 font-sans text-base">
                          · {p.company}
                        </span>
                      )}
                    </h3>
                    <a
                      href={`mailto:${p.email}`}
                      className="text-gold text-sm hover:underline"
                    >
                      {p.email}
                    </a>
                  </div>
                  <select
                    value={p.status}
                    onChange={(e) => updateProjectStatus(p.id, e.target.value)}
                    className={`bg-transparent border px-2 py-1 text-xs uppercase tracking-wider ${STATUS_COLOR[p.status]}`}
                    data-testid={`project-status-${p.id}`}
                  >
                    {STATUS_PROJECT.map((s) => (
                      <option key={s} value={s} className="bg-white">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm border-y border-neutral-200/70 py-3 my-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-400">
                      Budget
                    </div>
                    <div className="text-neutral-700">{p.budget || "-"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-400">
                      Timeline
                    </div>
                    <div className="text-neutral-700">{p.timeline || "-"}</div>
                  </div>
                </div>
                <p className="text-neutral-500 text-sm leading-relaxed whitespace-pre-line">
                  {p.description}
                </p>
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => deleteProject(p.id)}
                    className="text-neutral-400 hover:text-red-400 transition inline-flex items-center gap-2 text-xs uppercase tracking-wider"
                    data-testid={`project-delete-${p.id}`}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
            {projects.length === 0 && !loading && (
              <div className="lg:col-span-2 px-4 py-16 text-center text-neutral-400 border border-neutral-200">
                No project briefs yet.
              </div>
            )}
          </div>
        )}

        {tab === "availability" && <AvailabilityPanel />}
      </section>
    </div>
  );
}
